import type { ColumnProps } from "@monolith-forensics/monolith-ui";

import { monolithMoment } from "@/utils/date-format";
import type { AssetValidation } from "../asset-validations.types";

/** user_id → full_name, from the cached users list the table fetches. */
export type UsersById = Map<number, string>;

/**
 * A /validations row plus the resolved user names. The api returns ids only
 * (no join), and mfui copies `columnProps` into state once at mount, so a
 * render closure over a users map would stay empty forever — the names are
 * merged into the rows instead (withUserNames) so the cells re-render when
 * either query settles.
 */
export type AssetValidationRow = AssetValidation & {
  completed_by_name: string;
  created_by_name: string;
};

const userName = (usersById: UsersById, id: number | null) =>
  id == null ? "" : usersById.get(id) ?? "";

// A missing map entry (deleted user, users fetch failed) renders blank rather
// than the raw id.
export const withUserNames = (
  rows: AssetValidation[],
  usersById: UsersById
): AssetValidationRow[] =>
  rows.map((row) => ({
    ...row,
    completed_by_name: userName(usersById, row.completed_by_id),
    created_by_name: userName(usersById, row.created_by_id),
  }));

/**
 * `dataField` narrowed to `keyof AssetValidation` so a rename or typo fails
 * `yarn tsc` (same shape as LabAssetColumn), and so every column sorts by an
 * api column. Assignable to `ColumnProps`, so the array feeds `Table` directly.
 *
 * Sorting is opted into per column (VALIDATION_SORTABLE_COLUMNS on the api)
 * rather than table-wide: mfui's table-level `enableSorting` makes every
 * column sortable regardless of the column flag, and the api 400s on an
 * unknown sort_by. The user columns stay unsortable even though the api
 * accepts them: it sorts by raw user id, which is arbitrary to a user. Enable
 * them once the api joins users and sorts by name.
 */
export type AssetValidationColumn = Omit<ColumnProps, "dataField"> & {
  dataField: keyof AssetValidation;
};

type RowRender = { rowData: AssetValidationRow };

export const AssetValidationColumnDefinitions: AssetValidationColumn[] = [
  {
    dataField: "validation_date",
    caption: "Validation Date",
    dataType: "date",
    enableSorting: true,
    // YYYY-MM-DD, date-only: monolithMoment formats strings under 11 chars
    // without a time component.
    render: ({ rowData }: RowRender) =>
      monolithMoment({ timestamp: rowData.validation_date ?? undefined }),
  },
  {
    dataField: "next_validation_due",
    caption: "Next Validation Due",
    dataType: "date",
    enableSorting: true,
    render: ({ rowData }: RowRender) =>
      monolithMoment({ timestamp: rowData.next_validation_due ?? undefined }),
  },
  {
    dataField: "version",
    caption: "Version",
    enableSorting: true,
  },
  {
    // TEXT-backed and absent from VALIDATION_SORTABLE_COLUMNS, so no
    // enableSorting: a header click clears the current sort instead.
    dataField: "description",
    caption: "Description",
  },
  {
    dataField: "completed_by_id",
    caption: "Completed By",
    render: ({ rowData }: RowRender) => rowData.completed_by_name,
  },
  {
    dataField: "created_by_id",
    caption: "Created By",
    visible: false,
    render: ({ rowData }: RowRender) => rowData.created_by_name,
  },
  {
    dataField: "created_on",
    caption: "Created On",
    dataType: "datetime",
    visible: false,
    render: ({ rowData }: RowRender) =>
      monolithMoment({
        timestamp: rowData.created_on ?? undefined,
        includeTime: true,
      }),
  },
  {
    dataField: "updated_on",
    caption: "Updated On",
    dataType: "datetime",
    visible: false,
    render: ({ rowData }: RowRender) =>
      monolithMoment({
        timestamp: rowData.updated_on ?? undefined,
        includeTime: true,
      }),
  },
];
